/**
 * ImportHistory — /patients/import/history
 *
 * Paginated table of patient import sessions for the current organisation.
 * Scoped to CLINIC_ADMIN and PLATFORM_ADMINISTRATOR only.
 */
import React, {useContext, useEffect, useState} from 'react';
import {Alert, Col, Row, Table, Tag} from 'antd';
import DetailWrapper from '../components/common/DetailWrapper';
import {Navigate} from 'react-router-dom';
import {FormattedMessage, useIntl} from 'react-intl';
import {DentobookApplicationContext} from '../context/DentobookApplicationContext';
import {getPatientImportApi} from '../api/apiInstances';
import type {PatientImportListItem} from '../api-client';


const ALLOWED_ROLES = ['CLINIC_ADMIN'];
const PAGE_SIZE = 20;

function sourceLabel(source: string): string {
    switch (source) {
        case 'manual': return 'Manual';
        case 'csv': return 'CSV';
        case 'excel': return 'Excel';
        default: return source;
    }
}

function statusColor(status: string): string {
    switch (status) {
        case 'committed': return 'success';
        case 'partial': return 'warning';
        case 'validated': return 'processing';
        case 'draft': return 'default';
        default: return 'default';
    }
}

const ImportHistory: React.FC = () => {
    const intl = useIntl();
    const {principal} = useContext(DentobookApplicationContext);

    const [sessions, setSessions] = useState<PatientImportListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // All hooks must be declared before any conditional returns
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getPatientImportApi().listImportHistory(page, PAGE_SIZE)
            .then(resp => {
                if (!cancelled) {
                    setSessions(resp.data.items);
                    setTotal(resp.data.total);
                }
            })
            .catch(() => {
                if (!cancelled) setError(intl.formatMessage({id: 'import_history_load_error'}));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [page]);

    if (principal && !ALLOWED_ROLES.includes(principal.user?.role ?? '')) {
        return <Navigate to="/patients" replace/>;
    }

    const columns = [
        {
            title: <FormattedMessage id="import_history_date"/>,
            dataIndex: 'createdAt',
            width: 160,
            render: (v: string) => new Date(v).toLocaleString(),
        },
        {
            title: <FormattedMessage id="import_history_created_by"/>,
            dataIndex: 'createdByName',
            width: 160,
            render: (v: string | null | undefined) => v ?? '—',
        },
        {
            title: <FormattedMessage id="import_history_source"/>,
            dataIndex: 'source',
            width: 90,
            render: (v: string) => {
                const label = sourceLabel(v);
                const id = `import_source_${v}`;
                return <Tag>{intl.formatMessage({id, defaultMessage: label})}</Tag>;
            },
        },
        {
            title: <FormattedMessage id="import_history_file"/>,
            dataIndex: 'originalFilename',
            ellipsis: true,
            render: (v: string | null | undefined) => v ?? '—',
        },
        {
            title: <FormattedMessage id="import_history_total"/>,
            dataIndex: 'totalRows',
            width: 70,
            align: 'right' as const,
        },
        {
            title: <FormattedMessage id="import_history_committed"/>,
            dataIndex: 'committedRows',
            width: 90,
            align: 'right' as const,
            render: (v: number) => <span style={{color: v > 0 ? '#52c41a' : undefined}}>{v}</span>,
        },
        {
            title: <FormattedMessage id="import_history_warnings"/>,
            dataIndex: 'warningRows',
            width: 110,
            align: 'right' as const,
            render: (v: number) => v > 0 ? <span style={{color: '#fa8c16'}}>{v}</span> : '0',
        },
        {
            title: <FormattedMessage id="import_history_status"/>,
            dataIndex: 'status',
            width: 110,
            render: (v: string) => {
                const id = `import_status_${v}`;
                return (
                    <Tag color={statusColor(v)}>
                        {intl.formatMessage({id, defaultMessage: v})}
                    </Tag>
                );
            },
        },
    ];

    return (
        <DetailWrapper navigateBack="/patients/import">
            <Row className="mb-2">
                <Col span={24}>
                    <h3><FormattedMessage id="import_history_title"/></h3>
                </Col>
            </Row>

            {error && (
                <Alert
                    type="error"
                    message={error}
                    closable
                    onClose={() => setError(null)}
                    style={{marginBottom: 16}}
                />
            )}

            <Table<PatientImportListItem>
                dataSource={sessions}
                columns={columns}
                rowKey="id"
                loading={loading}
                size="small"
                scroll={{x: 'max-content'}}
                locale={{emptyText: <FormattedMessage id="import_history_empty"/>}}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total,
                    showSizeChanger: false,
                    onChange: setPage,
                    showTotal: (t) => `${t} sessions`,
                }}
            />
        </DetailWrapper>
    );
};

export default ImportHistory;
